// src/pages/TapToMeasure.js
import React, { useRef, useState, useEffect } from "react";
import Toast from "../components/Toast";
import SaveSurfaceProjectModal from "../components/SaveSurfaceProjectModal";
import API_BASE from "../api/apiBase";
import {
  DIM_COLORS,
  ttmDrawDimension,
  ttmDrawReticle,
  ttmDrawQrBox,
  ttmPillRect,
  ttmDrawPill,
  TTM_HALO,
} from "../utils/ttmDimensions";
import { detectQrCalibration, QR_WIDTH_INCHES } from "../utils/ttmQrCalibrate";
import { detectArucoCalibration } from "../utils/ttmArucoCalibrate"; // TEMP: ArUco validation
import { computeArucoSkew, arucoCaptureVerdict } from "../utils/ttmArucoSkew"; // TEMP: obliqueness metric
import { logQrSample } from "../utils/ttmQrLog"; // TEMP diagnostic logger
import "./TapToMeasure.css";

const STICK_LENGTH_INCHES = 11; // Surface Mode = AI Stick v2 (the trained artifact)

// ---- Capture guidance (Surface Mode, mobile). Tune from field data. ----
// Below this image min-dimension, the photo is a shrunk/emailed/screenshot
// image, not a real camera shot — block (a real phone shot is 3000px+).
const CAPTURE_MIN_DIM = 1200;
// Below this px/inch, the AI Stick is small in frame; result is rougher — warn.
const CAPTURE_WARN_PPI = 40;

// Pure evaluator: returns { severity:'ok'|'warn'|'block', check, message }.
const evaluateCaptureGuidance = ({ img, ppi, detected }) => {
  const w = img?.naturalWidth || img?.width || 0;
  const h = img?.naturalHeight || img?.height || 0;
  const minDim = Math.min(w, h);

  // Block: low-resolution photo.
  if (minDim && minDim < CAPTURE_MIN_DIM) {
    return {
      severity: "block",
      check: "resolution",
      message:
        "This photo is low-resolution. Use a photo taken directly with your phone camera for a reliable measurement.",
    };
  }
  // Block: AI Stick not detected (manual tap still available).
  if (!detected) {
    return {
      severity: "block",
      check: "not-detected",
      message:
        "AI Stick not found. Make sure it’s flat and fully visible, then retake — or tap the two ends to calibrate manually.",
    };
  }
  // Warn: stick small in frame (allowed, just rougher).
  if (ppi != null && ppi < CAPTURE_WARN_PPI) {
    return {
      severity: "warn",
      check: "small-stick",
      message:
        "The AI Stick looks small in frame. Move closer and retake for a sharper measurement.",
    };
  }
  return { severity: "ok", check: "ok", message: null };
};
const PREVIEW_FADE_DURATION_MS = 150;
const SNAP_TOLERANCE_DEG = 5;

// Below this px/inch, a reference is small enough in frame that perspective/
// foreshortening errors grow large and variable. Tune from ground-truth runs.
const LOW_PPI_THRESHOLD = 12;

const SOURCE_LABELS = { qr: "QR code", ai: "AI detection", manual: "Manual tap" };
const SOURCE_BADGE = {
  qr: "ttm-calib-badge--qr",
  ai: "ttm-calib-badge--ai",
  manual: "ttm-calib-badge--manual",
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

// Phones have no keyboard — only show the arrow-key hint on non-touch devices.
const IS_TOUCH_DEVICE =
  typeof window !== "undefined" &&
  ("ontouchstart" in window || (navigator.maxTouchPoints || 0) > 0);

// Small color dot used in the side panel so each readout matches its line color.
const ColorChip = ({ color }) => (
  <span
    style={{
      display: "inline-block",
      width: 9,
      height: 9,
      borderRadius: 3,
      background: color,
      marginRight: 6,
      verticalAlign: "middle",
    }}
  />
);

export default function TapToMeasure() {

  // -----------------------
  // Hooks (MUST be first)
  // -----------------------
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const lastFileRef = useRef(null);
  const previewStartRef = useRef(null);
  const zoomCanvasRef = useRef(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [mode, setMode] = useState("calibrate");
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [stickPoints, setStickPoints] = useState([]);
  const [widthPoints, setWidthPoints] = useState([]);
  const [lengthPoints, setLengthPoints] = useState([]);
  const [qrCorners, setQrCorners] = useState(null); // detected QR outline, if any
  const [stickPixels, setStickPixels] = useState(null);
  const [pixelsPerInch, setPixelsPerInch] = useState(null);
  const [widthFt, setWidthFt] = useState(null);
  const [lengthFt, setLengthFt] = useState(null);
  const [widthAngleDeg, setWidthAngleDeg] = useState(null);
  const [lengthAngleDeg, setLengthAngleDeg] = useState(null);
  // 'qr' | 'ai' | 'manual' | null
  const [calibrationSource, setCalibrationSource] = useState(null);
  const [hoverPoint, setHoverPoint] = useState(null);
  // Last-placed point, nudgeable with arrow keys: { kind:'stick'|'width'|'length', index:0|1 }
  const [activePoint, setActivePoint] = useState(null);
  const [precisionZoomEnabled, setPrecisionZoomEnabled] = useState(false);
  const [fileName, setFileName] = useState("");
  const [toastMessage, setToastMessage] = useState(null);
  const [livePreview, setLivePreview] = useState(null);
  const [aiStickWarning, setAiStickWarning] = useState(null);
  const [captureGuidance, setCaptureGuidance] = useState(null); // {severity, check, message}
  const [aiAttempted, setAiAttempted] = useState(false);

  // ----------------------------------------------------
  // Helpers
  // ----------------------------------------------------
  const distanceAndAngle = (p1, p2) => {
    if (!p1 || !p2) return { px: 0, angleDeg: 0 };
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const px = Math.sqrt(dx * dx + dy * dy);
    let angleDeg = (Math.atan2(dy, dx) * 180) / Math.PI;
    if (angleDeg < 0) angleDeg += 360;
    return { px, angleDeg };
  };

  const toFeetInches = (feetValue) => {
    if (typeof feetValue !== "number" || !isFinite(feetValue)) return null;
    const totalInches = Math.round(feetValue * 12);
    const feet = Math.floor(totalInches / 12);
    const inches = totalInches % 12;
    return { feet, inches };
  };

  const formatFeetInches = (feetValue) => {
    const fi = toFeetInches(feetValue);
    if (!fi) return "—";
    const { feet, inches } = fi;
    if (feet === 0 && inches === 0) return "0″";
    if (inches === 0) return `${feet}′`;
    if (feet === 0) return `${inches}″`;
    return `${feet}′ ${inches}″`;
  };

  const applySnap = (p1, p2, step) => {
    const { px, angleDeg } = distanceAndAngle(p1, p2);
    if (step === "width") {
      const near0 = Math.abs(angleDeg) <= SNAP_TOLERANCE_DEG || Math.abs(angleDeg - 360) <= SNAP_TOLERANCE_DEG;
      const near180 = Math.abs(angleDeg - 180) <= SNAP_TOLERANCE_DEG;
      if (near0 || near180) {
        const snapped = { x: p2.x, y: p1.y };
        return { p1, p2: snapped, px: Math.abs(snapped.x - p1.x), angleDeg: 0 };
      }
      return { p1, p2, px, angleDeg };
    }
    const near90 = Math.abs(angleDeg - 90) <= SNAP_TOLERANCE_DEG;
    const near270 = Math.abs(angleDeg - 270) <= SNAP_TOLERANCE_DEG;
    if (near90 || near270) {
      const snapped = { x: p1.x, y: p2.y };
      return { p1, p2: snapped, px: Math.abs(snapped.y - p1.y), angleDeg: 90 };
    }
    return { p1, p2, px, angleDeg };
  };

  // ----------------------------------------------------
  // CALIBRATION — QR (URL launch / rare close-up), ArUco probe, AI, manual
  // ----------------------------------------------------
  const applyQrCalibration = (qr) => {
    setQrCorners(qr.corners);
    setStickPoints([]);
    const roundedPx = Math.round(qr.qrPixelWidth * 2) / 2;
    setStickPixels(roundedPx);
    setPixelsPerInch(qr.pxPerInch);
    setCalibrationSource("qr");
    setAiStickWarning(null);
    setMode("width");
    if (qr.lowConfidence) {
      setToastMessage(
        "QR found but small in frame — measurements will be rougher.\n" +
        "Move closer so the QR is larger for best accuracy."
      );
    }
  };

  // STAGE 1 helper — log ArUco PPI vs AI-stick PPI, the % delta, and the
  // implied marker-size constant. On a clean head-on coplanar shot,
  // impliedArucoWidthIn IS the correct value for ARUCO_WIDTH_INCHES.
  const logCalibrationComparison = ({ file, img, aruco, stickPx }) => {
    const stickPPI = stickPx != null ? stickPx / STICK_LENGTH_INCHES : null;
    const arucoPPI = aruco ? aruco.pxPerInch : null;
    const arucoMarkerPx = aruco ? aruco.markerPixelWidth : null;

    const deltaPct =
      stickPPI && arucoPPI ? ((arucoPPI - stickPPI) / stickPPI) * 100 : null;
    const impliedArucoWidthIn =
      arucoMarkerPx != null && stickPPI ? arucoMarkerPx / stickPPI : null;

    const r2 = (n) => (n == null ? null : Math.round(n * 100) / 100);
    const r3 = (n) => (n == null ? null : Math.round(n * 1000) / 1000);

    // Obliqueness (shape-only; independent of print scale) from the marker corners.
    const skew = aruco ? computeArucoSkew(aruco.corners) : null;
    const verdict = arucoCaptureVerdict(skew);

    const ok = deltaPct != null && Math.abs(deltaPct) <= 2;
    console.log(
      "%c[CAL COMPARE]",
      `color:${ok ? "#16a34a" : "#b45309"};font-weight:bold`,
      {
        stickPPI: r2(stickPPI),
        arucoPPI: r2(arucoPPI),
        deltaPct: r2(deltaPct),
        impliedArucoWidthIn: r3(impliedArucoWidthIn), // set ARUCO_WIDTH_INCHES to this
        arucoMarkerPx: r2(arucoMarkerPx),
        stickPx: r2(stickPx),
        // obliqueness:
        compositeSkew: skew ? skew.compositeSkew : null,
        maxAngleDevDeg: skew ? skew.maxAngleDevDeg : null,
        sideRatioSkew: skew ? skew.sideRatioSkew : null,
        markerPx: skew ? skew.meanSidePx : null,
        verdict: verdict.reason, // ok | too-small | too-oblique | no-marker
      }
    );

    if (typeof logQrSample === "function") {
      logQrSample({
        file: file?.name || "",
        imgW: img.width,
        imgH: img.height,
        tier: "cal-compare",
        stickPx: r2(stickPx),
        stickPPI: r2(stickPPI),
        arucoDecoded: !!aruco,
        arucoMarkerId: aruco ? aruco.markerId : null,
        arucoPxWidth: r2(arucoMarkerPx),
        arucoPPI: r2(arucoPPI),
        deltaPct: r2(deltaPct),
        impliedArucoWidthIn: r3(impliedArucoWidthIn),
        // obliqueness columns:
        compositeSkew: skew ? skew.compositeSkew : null,
        maxAngleDevDeg: skew ? skew.maxAngleDevDeg : null,
        sideRatioSkew: skew ? skew.sideRatioSkew : null,
        diagRatio: skew ? skew.diagRatio : null,
        markerPx: skew ? skew.meanSidePx : null,
        verdict: verdict.reason,
      });
    }
  };

  const calibrateFromImage = async (file, img) => {
    // 1) QR — URL-launch marker; guard ON so only our own QR could set scale.
    let qr = null;
    try {
      qr = detectQrCalibration(img, { expectSubstring: "measuresmart" });
      if (qr) console.log("%c[QR TIER]", "color:#d9a300;font-weight:bold", { decoded: true });
    } catch (e) {
      console.error("QR detection threw:", e);
      qr = null;
    }

    // 2) ArUco — parallel probe (does NOT drive calibration in Stage 1).
    let aruco = null;
    try {
      aruco = detectArucoCalibration(img, { maxDetectWidth: img.naturalWidth || img.width });
      console.log(
        "%c[ARUCO TIER]",
        "color:#0d9488;font-weight:bold",
        aruco
          ? {
              decoded: true,
              markerId: aruco.markerId,
              markerPixelWidth: Math.round(aruco.markerPixelWidth * 10) / 10,
              pxPerInch: Math.round(aruco.pxPerInch * 100) / 100,
              lowConfidence: aruco.lowConfidence,
            }
          : "decoded: FALSE (no ArUco marker found)"
      );
    } catch (e) {
      console.error("[ARUCO TIER] threw:", e);
    }

    // If our own QR decoded (rare close-up), it drives scale as before.
    if (qr) {
      applyQrCalibration(qr);
      return;
    }

    // 3) AI stick — drives calibration in Stage 1 (trusted reference).
    const stickPx = await autoCalibrateWithAI(file, img);

    // Capture guidance (Surface Mode): resolution / detection / stick-size.
    const detected = stickPx != null;
    const ppi = detected ? stickPx / STICK_LENGTH_INCHES : null;
    setCaptureGuidance(evaluateCaptureGuidance({ img, ppi, detected }));

    // 4) Head-to-head log: ArUco PPI vs stick PPI + implied marker constant.
    logCalibrationComparison({ file, img, aruco, stickPx });
  };

  // Returns the rounded stick pixel width on success, or null if AI didn't
  // produce a scale (so the caller can log it and fall to manual).
  const autoCalibrateWithAI = async (file, img) => {
    try {
      if (!file || !img) return null;
      const formData = new FormData();
      formData.append("image", file);
      const res = await fetch(`${API_BASE}/api/detect-stick`, { method: "POST", body: formData });
      setAiAttempted(true);
      if (!res.ok) {
        setCalibrationSource(null);
        setAiStickWarning(
          "AI Stick not automatically detected.\n" +
          "Surface measurements require the AI Stick for calibration.\n" +
          "Tap the two ends of the AI Stick to calibrate manually.\n" +
          "For best results, ensure the stick is fully visible and not distorted by angle or perspective."
        );
        return null;
      }
      const data = await res.json();
      if (!data?.boundingBox) {
        setCalibrationSource(null);
        setToastMessage("AI Stick not automatically detected.\nTap the two ends of the AI Stick to calibrate manually.");
        return null;
      }
      const bbox = data.boundingBox;
      const imgWidth = img.width;
      const imgHeight = img.height;
      const leftPx = bbox.left * imgWidth;
      const topPx = bbox.top * imgHeight;
      const widthPx = bbox.width * imgWidth;
      const heightPx = bbox.height * imgHeight;
      const yCenter = topPx + heightPx / 2;
      const p1 = { x: leftPx, y: yCenter };
      const p2 = { x: leftPx + widthPx, y: yCenter };
      setQrCorners(null);
      setStickPoints([p1, p2]);
      setCalibrationSource("ai");
      setAiStickWarning(null);
      const roundedPx = Math.round(widthPx * 2) / 2;
      const ppi = roundedPx / STICK_LENGTH_INCHES;
      setStickPixels(roundedPx);
      setPixelsPerInch(ppi);
      setMode("width");

      // (small-stick warning is handled by capture guidance in calibrateFromImage)
      return roundedPx;
    } catch (err) {
      console.error("AI auto-calibration error:", err);
      setCalibrationSource(null);
      setAiAttempted(true);
      return null;
    }
  };

  // ----------------------------------------------------
  // DRAWING
  // ----------------------------------------------------
  useEffect(() => {
    if (!imageLoaded || !canvasRef.current || !imgRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(imgRef.current, 0, 0, canvas.width, canvas.height);

    const rect = canvas.getBoundingClientRect();
    const dispScale = rect.width > 0 ? rect.width / canvas.width : 1;
    const k = 1 / (dispScale || 1);
    const occupied = [];

    const drawMeasure = (points, label, color) => {
      if (!points || points.length === 0) return;
      if (points.length === 1) { ttmDrawReticle(ctx, points[0], color, k); return; }
      ttmDrawDimension(ctx, points[0], points[1], label, color, k, occupied);
    };

    if (qrCorners) {
      ttmDrawQrBox(ctx, qrCorners, `${QR_WIDTH_INCHES}″`, DIM_COLORS.stick, k, occupied);
    }
    drawMeasure(stickPoints, `${STICK_LENGTH_INCHES}″`, DIM_COLORS.stick);
    drawMeasure(widthPoints, formatFeetInches(widthFt), DIM_COLORS.width);
    drawMeasure(lengthPoints, formatFeetInches(lengthFt), DIM_COLORS.length);

    if (hoverPoint && imageLoaded) {
      const placingFirst =
        mode === "calibrate" ||
        (mode === "width" && widthPoints.length === 0) ||
        (mode === "length" && lengthPoints.length === 0);

      const placingSecond =
        pixelsPerInch &&
        ((mode === "width" && widthPoints.length === 1) ||
         (mode === "length" && lengthPoints.length === 1));

      if (placingSecond) {
        const p1 = mode === "width" ? widthPoints[0] : lengthPoints[0];
        const color = mode === "width" ? DIM_COLORS.width : DIM_COLORS.length;
        const now = performance.now ? performance.now() : Date.now();
        let alpha = 1;
        if (previewStartRef.current != null) {
          const dt = now - previewStartRef.current;
          alpha = Math.min(1, Math.max(0.15, dt / PREVIEW_FADE_DURATION_MS));
        }
        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.lineCap = "round";
        ctx.lineWidth = 2.5 * k + 4 * k; ctx.strokeStyle = TTM_HALO;
        ctx.beginPath(); ctx.moveTo(p1.x, p1.y); ctx.lineTo(hoverPoint.x, hoverPoint.y); ctx.stroke();
        ctx.lineWidth = 2.5 * k; ctx.strokeStyle = color;
        ctx.beginPath(); ctx.moveTo(p1.x, p1.y); ctx.lineTo(hoverPoint.x, hoverPoint.y); ctx.stroke();

        const dx = hoverPoint.x - p1.x, dy = hoverPoint.y - p1.y;
        const px = Math.sqrt(dx * dx + dy * dy);
        const label = formatFeetInches(px / pixelsPerInch / 12);
        if (label && label !== "—") {
          const mx = (p1.x + hoverPoint.x) / 2, my = (p1.y + hoverPoint.y) / 2;
          const box = ttmPillRect(ctx, label, mx, my - 16 * k, k);
          ttmDrawPill(ctx, box, label, color, k);
        }
        ctx.restore();
      }

      if (placingFirst || placingSecond) {
        const color =
          mode === "width" ? DIM_COLORS.width :
          mode === "length" ? DIM_COLORS.length : DIM_COLORS.stick;
        ttmDrawReticle(ctx, hoverPoint, color, k);
      }
    }

    const zoomCanvas = zoomCanvasRef.current;
    if (!zoomCanvas) return;
    const zctx = zoomCanvas.getContext("2d");
    zctx.clearRect(0, 0, zoomCanvas.width, zoomCanvas.height);
    if (!precisionZoomEnabled || !hoverPoint) {
      zctx.strokeStyle = "#b0c4de";
      zctx.lineWidth = 1;
      zctx.strokeRect(0.5, 0.5, zoomCanvas.width - 1, zoomCanvas.height - 1);
      return;
    }
    // Precision loupe. Sample the CLEAN full-resolution original image (not the
    // downscaled display canvas, and not the overlay-covered canvas) with
    // smoothing on, so the magnifier is crisp and the edge you're targeting
    // isn't obscured by measurement lines.
    const src = imgRef.current;
    if (!src) return;
    // hoverPoint is in canvas pixels; map to image pixels (equal here, but
    // stay robust if the canvas is ever capped below native resolution).
    const toImg = canvas.width > 0 ? src.width / canvas.width : 1;
    const hx = hoverPoint.x * toImg;
    const hy = hoverPoint.y * toImg;
    const ZOOM = 2.5;                                  // magnification factor
    const win = Math.round(zoomCanvas.width / ZOOM);   // source window in image px
    const sx = clamp(hx - win / 2, 0, Math.max(0, src.width - win));
    const sy = clamp(hy - win / 2, 0, Math.max(0, src.height - win));
    zctx.imageSmoothingEnabled = true;
    zctx.imageSmoothingQuality = "high";
    zctx.drawImage(src, sx, sy, win, win, 0, 0, zoomCanvas.width, zoomCanvas.height);
    zctx.strokeStyle = "#1e40af";
    zctx.lineWidth = 2;
    zctx.strokeRect(1, 1, zoomCanvas.width - 2, zoomCanvas.height - 2);
    const cx = zoomCanvas.width / 2;
    const cy = zoomCanvas.height / 2;
    // Crosshair with a dark halo so it reads on any background.
    zctx.lineWidth = 3;
    zctx.strokeStyle = "rgba(0,0,0,0.55)";
    zctx.beginPath();
    zctx.moveTo(cx - 12, cy); zctx.lineTo(cx + 12, cy);
    zctx.moveTo(cx, cy - 12); zctx.lineTo(cx, cy + 12);
    zctx.stroke();
    zctx.lineWidth = 1.25;
    zctx.strokeStyle = "rgba(255,255,255,0.95)";
    zctx.beginPath();
    zctx.moveTo(cx - 12, cy); zctx.lineTo(cx + 12, cy);
    zctx.moveTo(cx, cy - 12); zctx.lineTo(cx, cy + 12);
    zctx.stroke();
  }, [imageLoaded, stickPoints, widthPoints, lengthPoints, qrCorners, hoverPoint, mode, pixelsPerInch, precisionZoomEnabled, widthFt, lengthFt]);

  // Arrow-key fine-tuning: nudge the last-placed point 1px (10px with Shift),
  // recompute the affected measurement, and keep the loupe on the point.
  // Desktop only (phones have no keyboard; touch gets drag later).
  // NOTE: must stay ABOVE the apiMisconfigured early return — hooks can't be
  // called conditionally.
  useEffect(() => {
    if (!activePoint || !imageLoaded) return;
    const onKey = (e) => {
      const moves = {
        ArrowLeft: [-1, 0], ArrowRight: [1, 0],
        ArrowUp: [0, -1], ArrowDown: [0, 1],
      };
      if (!moves[e.key]) return;
      // Don't hijack arrows while typing in a field.
      const tag = (document.activeElement && document.activeElement.tagName) || "";
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      e.preventDefault();

      const step = e.shiftKey ? 10 : 1;
      const dx = moves[e.key][0] * step;
      const dy = moves[e.key][1] * step;

      const canvas = canvasRef.current;
      const w = canvas?.width ?? Infinity;
      const h = canvas?.height ?? Infinity;
      const move = (p) => ({
        x: Math.max(0, Math.min(p.x + dx, w)),
        y: Math.max(0, Math.min(p.y + dy, h)),
      });

      const { kind, index } = activePoint;
      const recompute2pt = (pts, setFt, setAng) => {
        if (pts.length === 2 && pts[0] && pts[1] && pixelsPerInch) {
          const { px, angleDeg } = distanceAndAngle(pts[0], pts[1]);
          setFt(px / pixelsPerInch / 12);
          setAng(angleDeg);
        }
      };

      let moved = null;
      if (kind === "stick") {
        const pts = [...stickPoints];
        if (!pts[index]) return;
        pts[index] = move(pts[index]); moved = pts[index];
        setStickPoints(pts);
        if (pts.length === 2 && pts[0] && pts[1]) {
          const { px } = distanceAndAngle(pts[0], pts[1]);
          const roundedPx = Math.round(px * 2) / 2;
          setStickPixels(roundedPx);
          setPixelsPerInch(roundedPx / STICK_LENGTH_INCHES);
        }
      } else if (kind === "width") {
        const pts = [...widthPoints];
        if (!pts[index]) return;
        pts[index] = move(pts[index]); moved = pts[index];
        setWidthPoints(pts);
        recompute2pt(pts, setWidthFt, setWidthAngleDeg);
      } else if (kind === "length") {
        const pts = [...lengthPoints];
        if (!pts[index]) return;
        pts[index] = move(pts[index]); moved = pts[index];
        setLengthPoints(pts);
        recompute2pt(pts, setLengthFt, setLengthAngleDeg);
      }
      if (moved) setHoverPoint(moved); // keep loupe centered on the nudged point
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [activePoint, imageLoaded, stickPoints, widthPoints, lengthPoints, pixelsPerInch]);

  // SAFE ZONE — ALL HOOKS DONE
  const apiMisconfigured = !API_BASE;
  if (apiMisconfigured) {
    return (
      <div style={{ padding: 40, color: "red" }}>
        API configuration missing. Surface Mode cannot start.
      </div>
    );
  }

  // ----------------------------------------------------
  // Pointer helpers
  // ----------------------------------------------------
  const getCanvasPointFromEvent = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    return { x: Math.max(0, Math.min(x, canvas.width)), y: Math.max(0, Math.min(y, canvas.height)) };
  };

  const handleCanvasPointerDown = (e) => {
    if (!imageLoaded) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (e.pointerId != null && canvas.setPointerCapture) {
      try { canvas.setPointerCapture(e.pointerId); } catch (_) {}
    }
    const point = getCanvasPointFromEvent(e);
    if (!point) return;

    if (mode === "calibrate") {
      const newPoints = stickPoints.length >= 2 ? [point] : [...stickPoints, point];
      setStickPoints(newPoints);
      setActivePoint({ kind: "stick", index: newPoints.length - 1 });
      setQrCorners(null);
      if (newPoints.length === 2) {
        const { px } = distanceAndAngle(newPoints[0], newPoints[1]);
        const roundedPx = Math.round(px * 2) / 2;
        setStickPixels(roundedPx);
        setPixelsPerInch(roundedPx / STICK_LENGTH_INCHES);
        setCalibrationSource("manual");
        setMode("width");
        setLivePreview(null);
        setHoverPoint(null);
        previewStartRef.current = null;
      } else {
        setCalibrationSource(null);
      }
      return;
    }

    if (mode === "width") {
      if (widthPoints.length === 0) {
        setWidthPoints([point]);
        setActivePoint({ kind: "width", index: 0 });
        previewStartRef.current = null;
        setLivePreview(null);
      } else {
        setHoverPoint(null);
        previewStartRef.current = null;
        if (pixelsPerInch) {
          const snapped = applySnap(widthPoints[0], point, "width");
          setWidthPoints([snapped.p1, snapped.p2]);
          setWidthFt(snapped.px / pixelsPerInch / 12);
          setWidthAngleDeg(snapped.angleDeg);
        } else {
          setWidthPoints([widthPoints[0], point]);
        }
        setActivePoint({ kind: "width", index: 1 });
        setLivePreview(null);
        setMode("length");
      }
      return;
    }

    if (mode === "length") {
      if (lengthPoints.length === 0) {
        setLengthPoints([point]);
        setActivePoint({ kind: "length", index: 0 });
        previewStartRef.current = null;
        setLivePreview(null);
      } else {
        setHoverPoint(null);
        previewStartRef.current = null;
        if (pixelsPerInch) {
          const snapped = applySnap(lengthPoints[0], point, "length");
          setLengthPoints([snapped.p1, snapped.p2]);
          setLengthFt(snapped.px / pixelsPerInch / 12);
          setLengthAngleDeg(snapped.angleDeg);
        } else {
          setLengthPoints([lengthPoints[0], point]);
        }
        setActivePoint({ kind: "length", index: 1 });
        setLivePreview(null);
        setMode("done");
      }
    }
  };

  const handleCanvasPointerMove = (e) => {
    if (!imageLoaded) return;
    const point = getCanvasPointFromEvent(e);
    if (!point) return;
    setHoverPoint(point);
    const inPreviewMode =
      pixelsPerInch &&
      ((mode === "width" && widthPoints.length === 1) ||
        (mode === "length" && lengthPoints.length === 1));
    if (!inPreviewMode) { setLivePreview(null); previewStartRef.current = null; return; }
    if (previewStartRef.current == null) previewStartRef.current = performance.now ? performance.now() : Date.now();
    const activePoints = mode === "width" ? widthPoints : lengthPoints;
    const { px, angleDeg } = distanceAndAngle(activePoints[0], point);
    const feet = px / pixelsPerInch / 12;
    setLivePreview({ text: `${formatFeetInches(feet)} (${angleDeg.toFixed(1)}°)`, feet, angleDeg, px, step: mode });
  };

  const handleCanvasPointerLeave = () => setHoverPoint(null);

  // ----------------------------------------------------
  // Reset
  // ----------------------------------------------------
  const resetMeasurements = () => {
    setMode("calibrate");
    setStickPoints([]); setWidthPoints([]); setLengthPoints([]);
    setQrCorners(null);
    setStickPixels(null); setPixelsPerInch(null);
    setWidthFt(null); setLengthFt(null);
    setWidthAngleDeg(null); setLengthAngleDeg(null);
    setCalibrationSource(null); setHoverPoint(null);
    setActivePoint(null);
    previewStartRef.current = null; setLivePreview(null);
    setAiAttempted(false);
    setCaptureGuidance(null);
    if (imgRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(imgRef.current, 0, 0, canvas.width, canvas.height);
    }
  };

  const restartCalibration = () => {
    if (!imgRef.current || !lastFileRef.current) return;
    resetMeasurements();
    calibrateFromImage(lastFileRef.current, imgRef.current);
  };

  const areaSqFt =
    typeof widthFt === "number" && typeof lengthFt === "number" &&
    isFinite(widthFt) && isFinite(lengthFt) ? widthFt * lengthFt : null;

  const canSave =
    mode === "done" &&
    typeof widthFt === "number" && isFinite(widthFt) && widthFt > 0 &&
    typeof lengthFt === "number" && isFinite(lengthFt) && lengthFt > 0 &&
    !!canvasRef.current;

  // ----------------------------------------------------
  // Image upload
  // ----------------------------------------------------
  const handleImageUpload = (file) => {
    if (!file) return;
    setFileName(file.name);
    lastFileRef.current = file;
    const objectURL = URL.createObjectURL(file);
    const img = new Image();
    img.src = objectURL;
    img.onload = () => {
      imgRef.current = img;
      setImageLoaded(true);
      setAiStickWarning(null);
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      setMode("calibrate");
      setStickPoints([]); setWidthPoints([]); setLengthPoints([]);
      setQrCorners(null);
      setStickPixels(null); setPixelsPerInch(null);
      setWidthFt(null); setLengthFt(null);
      setWidthAngleDeg(null); setLengthAngleDeg(null);
      setCalibrationSource(null); setHoverPoint(null);
      setActivePoint(null);
      setLivePreview(null); previewStartRef.current = null;
      setAiAttempted(false);
      setCaptureGuidance(null);
      calibrateFromImage(file, img);
      try { URL.revokeObjectURL(objectURL); } catch (_) {}
    };
    img.onerror = () => {
      try { URL.revokeObjectURL(objectURL); } catch (_) {}
      setToastMessage("Failed to load image. Please try another file.");
    };
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    handleImageUpload(file);
    e.target.value = "";
  };

  // ----------------------------------------------------
  // Step bar state
  // ----------------------------------------------------
  const stepIndex = { calibrate: 1, width: 2, length: 3, done: 4 }[mode] ?? 1;
  const steps = [
    { n: 1, label: "Upload photo", sub: "With AI Stick visible" },
    { n: 2, label: "Calibrate", sub: "QR, AI, or manual" },
    { n: 3, label: "Measure width", sub: "Tap two points" },
    { n: 4, label: "Measure length", sub: "Tap two points" },
  ];

  const showPreviewCard =
    pixelsPerInch &&
    ((mode === "width" && widthPoints.length === 1) ||
      (mode === "length" && lengthPoints.length === 1));

  const needsManualCalibration =
    imageLoaded && aiAttempted && !pixelsPerInch && mode === "calibrate";

  const sourceLabel = SOURCE_LABELS[calibrationSource] || "—";
  const refInches = calibrationSource === "qr" ? QR_WIDTH_INCHES : STICK_LENGTH_INCHES;

  // ----------------------------------------------------
  // Render
  // ----------------------------------------------------
  return (
    <div className="ttm-root">

      {/* Step bar */}
      <div className="ttm-stepbar">
        {steps.map((s, i) => (
          <React.Fragment key={s.n}>
            <div className={`ttm-step ${imageLoaded && stepIndex >= s.n ? "ttm-step--active" : ""} ${imageLoaded && stepIndex > s.n ? "ttm-step--done" : ""}`}>
              <div className="ttm-step-num">
                {imageLoaded && stepIndex > s.n ? "✓" : s.n}
              </div>
              <div className="ttm-step-text">
                <strong>{s.label}</strong>
                <span>{s.sub}</span>
              </div>
            </div>
            {i < steps.length - 1 && <div className="ttm-step-connector" />}
          </React.Fragment>
        ))}
      </div>

      <div className="ttm-body">

        {/* Upload card */}
        <div className="ttm-card">
          <div className="ttm-card-header">
            <span className="ttm-card-title">Upload photo</span>
            {fileName && (
              <span className="ttm-filename">File: <strong>{fileName}</strong></span>
            )}
          </div>

          <input
            id="tap-measure-file-input"
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleFileInputChange}
          />

          <div className="ttm-controls">
            <button
              className="ttm-btn ttm-btn--primary"
              onClick={() => document.getElementById("tap-measure-file-input")?.click()}
            >
              Upload file
            </button>

            <button
              className="ttm-btn ttm-btn--ghost"
              onClick={() => document.getElementById("tap-measure-file-input")?.click()}
            >
              Replace image
            </button>

            <button
              className="ttm-btn ttm-btn--secondary"
              onClick={restartCalibration}
              disabled={!imageLoaded}
            >
              Restart calibration
            </button>
          </div>

          <label className="ttm-zoom-toggle" style={{ marginTop: 14 }}>
            <input
              type="checkbox"
              checked={precisionZoomEnabled}
              onChange={(e) => setPrecisionZoomEnabled(e.target.checked)}
            />
            <strong>Precision Tap Zoom</strong>
            <span style={{ color: "#6b7280", fontWeight: 500 }}>(magnified view near your cursor)</span>
          </label>

          {imageLoaded && (
            <p className="ttm-tip" style={{ marginTop: 10 }}>
              Tip: Tap once to anchor, move your cursor to stretch the line, then tap again to confirm.
              Live preview shows in the <strong>right panel</strong>.
            </p>
          )}
        </div>

        {/* Warning banner */}
        {aiStickWarning && (
          <div className="ttm-warning">
            {aiStickWarning}
          </div>
        )}

        {/* Workspace */}
        <div className="ttm-workspace">

          {/* Canvas */}
          <div className="ttm-canvas-wrap">
            <canvas
              ref={canvasRef}
              onPointerDown={handleCanvasPointerDown}
              onPointerMove={handleCanvasPointerMove}
              onPointerLeave={handleCanvasPointerLeave}
              style={{ cursor: imageLoaded ? "none" : "default", touchAction: "none" }}
            />

            {/* Capture guidance (mobile): persists below the photo so the user
                can read it while deciding to retake. Tiered: block vs warn. */}
            {imageLoaded && captureGuidance && captureGuidance.severity !== "ok" && (
              <div className={`ttm-capture-banner ttm-capture-banner--${captureGuidance.severity}`}>
                <div className="ttm-capture-banner-text">{captureGuidance.message}</div>
                <div className="ttm-capture-banner-actions">
                  <button
                    className="ttm-capture-btn ttm-capture-btn--primary"
                    onClick={() => document.getElementById("tap-measure-file-input")?.click()}
                  >
                    Retake
                  </button>
                  <button
                    className="ttm-capture-btn ttm-capture-btn--ghost"
                    onClick={() => setCaptureGuidance(null)}
                  >
                    {captureGuidance.severity === "block" ? "Measure anyway" : "Got it"}
                  </button>
                </div>
              </div>
            )}

            {/* Desktop fine-tune hint: arrow keys nudge the last-placed point. */}
            {imageLoaded && activePoint && !IS_TOUCH_DEVICE && (
              <div className="ttm-nudge-hint">
                Fine-tune: <strong>arrow keys</strong> nudge the last point · <strong>Shift</strong> = 10px steps
              </div>
            )}
          </div>

          {/* Right panel */}
          <div className="ttm-panel">

            {/* Precision Zoom */}
            <div className="ttm-zoom-card">
              <p className="ttm-zoom-card-title">Precision Tap Zoom</p>
              <p className="ttm-zoom-card-sub">
                When enabled, shows a magnified view around your cursor so taps land exactly where you expect.
              </p>
              <canvas
                ref={zoomCanvasRef}
                width={280}
                height={280}
                className="ttm-zoom-canvas"
              />
            </div>

            {/* Calibration + Measurement */}
            <div className="ttm-info-card">
              <p className="ttm-info-section-title">Calibration</p>

              {needsManualCalibration && (
                <div className="ttm-manual-required">
                  <strong>Manual calibration required</strong>
                  <span>The AI Stick wasn’t detected. Tap both ends of the stick on the photo to set the scale.</span>
                </div>
              )}

              <div className="ttm-info-row">
                <span className="ttm-info-label">
                  <ColorChip color={DIM_COLORS.stick} />Source
                </span>
                <span
                  className="ttm-info-value"
                  style={needsManualCalibration ? { color: "#b45309", fontWeight: 800 } : undefined}
                >
                  {stickPixels
                    ? sourceLabel
                    : (needsManualCalibration ? "Manual required" : "—")}
                </span>
              </div>
              <div className="ttm-info-row">
                <span className="ttm-info-label">{calibrationSource === "qr" ? "QR px" : "Stick px"}</span>
                <span className="ttm-info-value">{stickPixels ? stickPixels.toFixed(1) : "—"}</span>
              </div>
              <div className="ttm-info-row">
                <span className="ttm-info-label">PPI</span>
                <span className="ttm-info-value">{pixelsPerInch ? pixelsPerInch.toFixed(2) : "—"}</span>
              </div>

              <div className="ttm-info-divider" />

              <p className="ttm-info-section-title">Measurement</p>
              <div className="ttm-info-row">
                <span className="ttm-info-label">
                  <ColorChip color={DIM_COLORS.width} />Width
                </span>
                <span className="ttm-info-value--highlight">
                  {formatFeetInches(widthFt)}
                  {widthAngleDeg !== null && (
                    <span className="ttm-info-angle"> ({widthAngleDeg.toFixed(1)}°)</span>
                  )}
                </span>
              </div>
              <div className="ttm-info-row">
                <span className="ttm-info-label">
                  <ColorChip color={DIM_COLORS.length} />Length
                </span>
                <span className="ttm-info-value--highlight">
                  {formatFeetInches(lengthFt)}
                  {lengthAngleDeg !== null && (
                    <span className="ttm-info-angle"> ({lengthAngleDeg.toFixed(1)}°)</span>
                  )}
                </span>
              </div>
              <div className="ttm-info-row">
                <span className="ttm-info-label">Area</span>
                <span className="ttm-info-value--highlight">
                  {areaSqFt ? `${areaSqFt.toFixed(2)} sq ft` : "—"}
                </span>
              </div>
              <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
                Snap: ±{SNAP_TOLERANCE_DEG}° ({mode === "length" ? "vertical" : "horizontal"})
              </div>

              {/* Live preview */}
              {showPreviewCard && (
                <div className="ttm-live-preview">
                  Live: {livePreview?.text || "—"}
                  <div className="ttm-live-preview-sub">Move cursor, then tap to set point 2.</div>
                </div>
              )}

              {/* Accuracy notice */}
              <div className="ttm-accuracy-notice">
                <div className="ttm-accuracy-notice-title">Accuracy & Usage Notice</div>
                <div>Measurements are typically within <strong>±2–4 inches</strong> depending on photo angle and calibration placement.</div>
                <div style={{ marginTop: 6 }}>Intended for <strong>project estimation</strong>, not final fabrication.</div>
                <div style={{ marginTop: 6 }}><strong>Best accuracy:</strong> place the AI Stick on the <strong>same surface</strong> as the measurement edge.</div>
              </div>
            </div>
          </div>
        </div>

        {/* Results card */}
        <div className="ttm-card">
          <div className="ttm-card-header">
            <span className="ttm-card-title">Measurements</span>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="ttm-btn ttm-btn--ghost" onClick={resetMeasurements}>
                Reset all steps
              </button>
              <button
                type="button"
                className={`ttm-btn ${canSave ? "ttm-btn--success" : "ttm-btn--disabled-save"}`}
                onClick={() => canSave && setShowSaveModal(true)}
                disabled={!canSave}
                title={canSave ? "Save this surface measurement as a project." : "Complete Width + Length (Step 4) to enable saving."}
              >
                Save project
              </button>
            </div>
          </div>

          {/* Metric strip */}
          <div className="ttm-results-grid">
            <div className="ttm-metric">
              <div className="ttm-metric-label">Width</div>
              <div className={widthFt ? "ttm-metric-value" : "ttm-metric-value--muted"}>
                {formatFeetInches(widthFt)}
              </div>
            </div>
            <div className="ttm-metric">
              <div className="ttm-metric-label">Length</div>
              <div className={lengthFt ? "ttm-metric-value" : "ttm-metric-value--muted"}>
                {formatFeetInches(lengthFt)}
              </div>
            </div>
            <div className="ttm-metric">
              <div className="ttm-metric-label">Area</div>
              <div className={areaSqFt ? "ttm-metric-value" : "ttm-metric-value--muted"}>
                {areaSqFt ? `${areaSqFt.toFixed(1)} sq ft` : "—"}
              </div>
            </div>
          </div>

          <div className="ttm-results-details">
            <div>
              <strong>Calibration source:</strong>{" "}
              {stickPixels ? (
                <span className={`ttm-calib-badge ${SOURCE_BADGE[calibrationSource] || "ttm-calib-badge--manual"}`}>
                  {sourceLabel}
                </span>
              ) : needsManualCalibration ? (
                <span className="ttm-calib-badge ttm-calib-badge--required">Manual required</span>
              ) : "—"}
            </div>
            <div><strong>Calibration reference:</strong> {stickPixels ? `${stickPixels.toFixed(2)} px (for ${refInches}")` : "— (waiting for QR, AI, or manual)"}</div>
            <div><strong>Scale:</strong> {pixelsPerInch ? `${pixelsPerInch.toFixed(2)} px per inch` : "—"}</div>
            <div>
              <strong>Width:</strong> {formatFeetInches(widthFt)}
              {widthAngleDeg !== null && <span style={{ fontSize: 12, color: "#9ca3af" }}> ({widthAngleDeg.toFixed(1)}°)</span>}
            </div>
            <div>
              <strong>Length:</strong> {formatFeetInches(lengthFt)}
              {lengthAngleDeg !== null && <span style={{ fontSize: 12, color: "#9ca3af" }}> ({lengthAngleDeg.toFixed(1)}°)</span>}
            </div>
            <div><strong>Area (sq ft):</strong> {areaSqFt ? areaSqFt.toFixed(1) : "—"}</div>
          </div>

          <p className="ttm-results-note">
            Note: In <strong>Surface Mode</strong>, saving uploads the surface image and writes a single project entry
            into the shared <code>projects</code> collection for contractors to review.
          </p>
        </div>

      </div>

      {/* Toast */}
      {toastMessage && (
        <Toast message={toastMessage} onClose={() => setToastMessage(null)} style={{ top: "180px", right: "80px" }} />
      )}

      {/* Save Modal */}
      <SaveSurfaceProjectModal
        isOpen={showSaveModal}
        onClose={(didSave) => {
          setShowSaveModal(false);
          if (didSave) setToastMessage("Surface project saved. Check Contractor Dashboard.");
        }}
        measurements={{ widthFt, lengthFt, areaSqFt: areaSqFt ?? null, scale: pixelsPerInch, source: calibrationSource }}
        canvasRef={canvasRef}
      />
    </div>
  );
}
